import { useEffect } from 'react';
import CompareHero from './components/CompareHero';
import ComparisonTable from './components/ComparisonTable';
import DealerTactics from './components/DealerTactics';
import DealerBingo from './components/DealerBingo';
import PricingBreakdown from './components/PricingBreakdown';
import CustomerWins from './components/CustomerWins';
import CompetitorGrid from './components/CompetitorGrid';
import ConsultationCta from './components/ConsultationCta';
import FinalCta from './components/FinalCta';
import HowWePriceSection from './components/HowWePriceSection';
import { injectJsonLd, removeJsonLd, buildWebPageSchema, buildBreadcrumbSchema } from '../../lib/schemaBuilder';
import Breadcrumb from '../../components/feature/Breadcrumb';
import { useSEOHead } from '../../hooks/useSEOHead';

const BASE = 'https://rockhoundauto.com';

const COMPARE_FAQ_ITEMS = [
  {
    question: 'How does Rockhound Auto compare to traditional dealerships?',
    answer: 'Rockhound Auto charges no hidden fees, no dealer markups, and no pressure add-ons. Traditional dealerships typically add 15–25% above wholesale cost plus doc fees and finance office upsells. We charge one transparent flat fee.',
  },
  {
    question: 'Does Rockhound Auto have hidden fees?',
    answer: 'No. We charge a single flat broker fee of $995. There are no doc fees, no dealer markups, no GAP insurance upsells, and no surprise charges at signing. Every cost is disclosed upfront before you commit.',
  },
  {
    question: 'Can Rockhound Auto beat dealership prices?',
    answer: 'Yes. Because we source vehicles from wholesale dealer-only auctions (Manheim, ADESA, IAA) and pass that pricing directly to you, our customers typically save 10–18% compared to buying the same vehicle at a traditional dealership.',
  },
  {
    question: 'Is Rockhound Auto a licensed dealership?',
    answer: 'Yes, Rockhound Auto is a fully licensed Oregon auto dealer and broker. We are bonded, insured, and operate with complete transparency — unlike private sellers or unlicensed brokers.',
  },
  {
    question: 'How does Rockhound Auto handle financing compared to dealerships?',
    answer: 'We work with multiple lenders to find you the best rate — we don\'t mark up the interest rate the way dealership finance offices do. All credit types are welcome, including first-time buyers and those rebuilding credit.',
  },
];

export default function ComparePage() {
  useSEOHead({
    title: 'Rockhound Auto vs Local Dealers | Honest Pricing',
    description: 'Compare Rockhound Auto vs Warrenton Kia, Lums Toyota, Astoria Ford & Oceancrest Chevrolet. Save $3,000–$8,000 with transparent pricing, zero hidden fees, and honest service on the Oregon Coast.',
    keywords: 'Rockhound Auto vs Warrenton Kia, better than Lums Toyota, Astoria Ford alternative, auto broker Warrenton Oregon, used cars Pacific Northwest',
    canonicalUrl: `${BASE}/compare`,
    geoRegion: 'US-OR',
    geoPlacename: 'Warrenton, Oregon',
    geoPosition: '46.1651;-123.9237',
  });

  useEffect(() => {
    const webpageSchema = buildWebPageSchema({
      name: 'Compare Rockhound Auto vs Local Dealers | Honest Pricing Comparison',
      description: 'See how Rockhound Auto compares to traditional dealerships. Transparent pricing, no hidden fees, and honest service in Southwest Washington.',
      url: `${BASE}/compare`,
      breadcrumbs: [
        { name: 'Home', item: BASE },
        { name: 'Compare', item: `${BASE}/compare` },
      ],
    });

    const breadcrumbSchema = buildBreadcrumbSchema([
      { name: 'Home', item: BASE },
      { name: 'Compare', item: `${BASE}/compare` },
    ]);

    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: COMPARE_FAQ_ITEMS.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    };

    injectJsonLd('schema-compare-webpage', webpageSchema);
    injectJsonLd('schema-compare-breadcrumb', breadcrumbSchema);
    injectJsonLd('schema-compare-faq', faqSchema);

    return () => {
      removeJsonLd('schema-compare-webpage');
      removeJsonLd('schema-compare-breadcrumb');
      removeJsonLd('schema-compare-faq');
    };
  }, []);

  return (
    <>
      <Breadcrumb items={[{ label: 'Home', path: '/' }, { label: 'Compare' }]} />

      <CompareHero />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Expert Perspective Block */}
        <div className="mb-16 bg-gradient-to-br from-red-50 to-orange-50 border-l-4 border-red-500 rounded-lg p-6 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center">
                <i className="ri-contrast-2-line text-2xl text-white"></i>
              </div>
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 mb-2">Why the Numbers Look Different</h2>
              <p className="text-gray-700 leading-relaxed">
                Franchise dealers carry overhead costs — showrooms, large staff, advertising — that get built into the price through doc fees, dealer prep, and market adjustments. These are real costs, but they're often disclosed late in the process. We operate leaner and publish our single flat fee of $995 upfront, before you commit to anything. When you compare total out-the-door prices rather than sticker prices, the difference becomes clear. Always ask for the final number first.
              </p>
            </div>
          </div>
        </div>

        <HowWePriceSection />
        <CompetitorGrid />
        <PricingBreakdown />
        <DealerTactics />
        <DealerBingo />
        <CustomerWins />
        <ConsultationCta />
        <FinalCta />
      </div>
    </>
  );
}
